/**
 * Which view the New Connection dialog shows, and what Escape does there (Option C, C1, plan §3.1 / §3.6). Pure.
 *
 * - Create mode opens on the start view (no engine chosen), or straight in the form when a prefill handoff (palette
 *   "New Connection from Clipboard", "Add SAS as a Jam connection") or `initialEngine` names the engine.
 * - Edit mode opens in the form, with no start view and no rail; the engine is locked (D1).
 */
import type { DatabaseEngine } from '../../components/connectionFormSchema';

/** How an engine was chosen (`ui:connection_engine_selected.source`, the draft operations' `source`). */
export type EngineSource =
  | 'card'
  | 'filter_enter'
  | 'rail'
  | 'compact_select'
  | 'paste'
  | 'azure_pick'
  | 'preset'
  | 'prefill'
  | 'edit';

export type DialogView = { kind: 'start' } | { kind: 'form'; arrivedBy: EngineSource };

export type InitialViewOptions = {
  isEditMode: boolean;
  /** The engine a host or test asked the dialog to open in. */
  initialEngine?: DatabaseEngine;
  /** The engine of a pending prefill handoff this dialog was opened for. */
  prefillEngine?: DatabaseEngine;
};

export function initialDialogView({ isEditMode, initialEngine, prefillEngine }: InitialViewOptions): DialogView {
  if (isEditMode) return { kind: 'form', arrivedBy: 'edit' };
  if (prefillEngine || initialEngine) return { kind: 'form', arrivedBy: 'prefill' };
  return { kind: 'start' };
}

/** A pick lands in the form; returning to "All engines" lands on the start view. */
export function formView(arrivedBy: EngineSource): DialogView {
  return { kind: 'form', arrivedBy };
}

export const START_VIEW: DialogView = { kind: 'start' };

/** Focus after a pick: a manual pick goes to Name (owner's rule); a prefill to the first required empty field. */
export function isManualPick(source: EngineSource): boolean {
  return source === 'card' || source === 'filter_enter' || source === 'rail' || source === 'compact_select';
}

/** How a saved draft was filled (`feature:connection_saved.origin`, C3): by hand, or last by a patch. */
export type SaveOrigin = 'manual' | 'paste' | 'prefill' | 'azure' | 'preset';

export function saveOriginOf(source: EngineSource | undefined): SaveOrigin {
  if (source === 'paste') return 'paste';
  if (source === 'prefill') return 'prefill';
  if (source === 'azure_pick') return 'azure';
  if (source === 'preset') return 'preset';
  return 'manual';
}

export type EscapeStep = 'close-paste' | 'clear-filter' | 'back-to-start' | 'close';

export type EscapeContext = {
  isEditMode: boolean;
  view: DialogView['kind'];
  filter: string;
  /** The form view's paste field is open (C3; the start view pastes into its filter). */
  pasteOpen: boolean;
};

/**
 * What Escape does once no nested layer took it (a native select popup, a nested dialog and the Add from… menu consume
 * it first — Radix's layer stack): on the start view it clears the filter (a detection card with it); in the
 * create-mode form it first closes the paste field (C3), then steps back to the start view and keeps the draft (D11);
 * otherwise (an empty start view, edit mode) it closes the dialog.
 */
export function escapeStep({ isEditMode, view, filter, pasteOpen }: EscapeContext): EscapeStep {
  if (isEditMode) return 'close';
  if (view === 'form') return pasteOpen ? 'close-paste' : 'back-to-start';
  if (filter.length > 0) return 'clear-filter';
  return 'close';
}
